/*
  Client-side wishlist. Shopify has no native wishlist object/API - this is
  the standard no-app pattern: persist real product data in localStorage
  (not just IDs, so the wishlist page never needs a network round-trip to
  render), toggle from any heart button on the site via a shared custom
  element, and broadcast a window event so the header badge and any open
  wishlist page/drawer can react without a page reload.
*/

(function () {
  const STORAGE_KEY = 'toylivo:wishlist';

  function readAll() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeAll(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      /* localStorage unavailable (private mode, quota) - wishlist silently no-ops rather than throwing */
    }
    window.dispatchEvent(new CustomEvent('toylivo:wishlist:change', { detail: { items } }));
  }

  function isSaved(id) {
    return readAll().some((item) => String(item.id) === String(id));
  }

  function toggle(product) {
    const items = readAll();
    const index = items.findIndex((item) => String(item.id) === String(product.id));
    if (index === -1) {
      items.push(product);
      writeAll(items);
      return true;
    }
    items.splice(index, 1);
    writeAll(items);
    return false;
  }

  window.ToylivoWishlist = { readAll, toggle, isSaved };

  class ToylivoWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.productId = this.dataset.productId;
      this.refresh();
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        const saved = toggle({
          id: this.productId,
          handle: this.dataset.productHandle,
          title: this.dataset.productTitle,
          image: this.dataset.productImage,
          price: this.dataset.productPrice,
          url: this.dataset.productUrl,
        });
        this.setState(saved);
      });
      window.addEventListener('toylivo:wishlist:change', () => this.refresh());
    }

    refresh() {
      this.setState(isSaved(this.productId));
    }

    setState(saved) {
      this.classList.toggle('is-saved', saved);
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
    }
  }

  customElements.define('toylivo-wishlist-button', ToylivoWishlistButton);

  class ToylivoWishlistCount extends HTMLElement {
    connectedCallback() {
      this.refresh();
      window.addEventListener('toylivo:wishlist:change', () => this.refresh());
    }

    refresh() {
      const count = readAll().length;
      this.textContent = count > 0 ? count : '';
      this.hidden = count === 0;
    }
  }

  customElements.define('toylivo-wishlist-count', ToylivoWishlistCount);
})();
